/**
 * tokens.ts — THE single source of design-token values.
 *
 * S-TIER FOUNDATION (slice 1): every colour value below is now a `var(--…)`
 * REFERENCE into src/styles/klaxiora.css — the adopted S-Tier design system
 * (docs/design/stier/, the VISUAL authority per CLAUDE.md). The token KEYS and
 * the three consumption paths are UNCHANGED, which is the whole point of the
 * indirection: all 48 direct importers, the tailwind.config.ts spread and
 * index.css's theme() references keep resolving with zero call-site edits, and
 * the app gains a light theme for free because every one of these names now
 * resolves per `data-theme` on <html>.
 *
 * What that means when you edit this file:
 *   - A colour VALUE is never written here any more. Change it in
 *     klaxiora.css, which is the machine-readable form of the handoff.
 *   - This file decides the MAPPING: which app semantic rides which system
 *     token. tokens.test.ts pins the mapping AND klaxiora.css's literals, so
 *     drift on either side fails the suite.
 *   - `--k-*` names are the documented app EXTENSIONS (an info/blue family,
 *     dept identity hues, a disabled ink step, the scrim) — families the
 *     system does not ship, authored in klaxiora.css in its own oklch idiom.
 *
 * SVG presentation attributes resolve var() too: recharts writes these tokens
 * straight into `fill` / `stroke` / tick `fill`, measured pixel-identical to
 * the literal and re-resolving live on a theme flip (docs/design/stier/
 * ADOPTION.md §5.2). There is NO resolver hook, and §5.2 forbids adding one.
 *
 * One definition, consumed three ways:
 *   - tailwind.config.ts spreads these into theme.extend  → className usage
 *   - src/index.css reads them via theme('colors...')     → .closr-* classes
 *   - components import them directly                     → style={{}} maps
 *
 * Naming: semantic only. `ink-*` (not `text-*`: Mission Control's live
 * `text-primary`/`text-muted` config keys would collide).
 */

// ── Surfaces ──────────────────────────────────────────────────────────────────
// The system has five surfaces (bg → panel → panel2 → line/hair). The app's
// finer-grained roles collapse onto them: the canvas is --bg, every raised
// surface (sidebar, header, card) is --panel, every inset/hover/control fill
// is --panel2. Inputs sit on --bg, matching .k-search.
export const surface = {
  app: 'var(--bg)', // dash-shell canvas
  content: 'var(--bg)', // main content area
  sidebar: 'var(--k-sidebar-bg)', // .k-sidebar — its own ground since slice 2 (the boards' value in light; --panel in dark)
  header: 'var(--k-topbar-bg)', // .k-topbar — translucent over the page in light; --panel in dark
  card: 'var(--panel)', // .k-card
  control: 'var(--panel2)', // chips, selects, outline buttons
  panel: 'var(--panel2)', // static chip/box fills
  'nav-hover': 'var(--panel2)', // .k-navrow:hover
  'nav-active': 'var(--panel2)', // .k-navrow[aria-current="page"]
  'row-hover': 'var(--panel2)', // .k-row:hover
  input: 'var(--bg)', // .k-search ground
} as const

export const track = {
  DEFAULT: 'var(--panel2)', // skeleton rows + meter tracks
  strong: 'var(--panel2)', // skeleton title bars
} as const

// ── Borders ───────────────────────────────────────────────────────────────────
// The system has two: --hair (internal dividers) and --line (outlines). Every
// app edge role maps onto one of them; the one hover edge is --text3, which is
// what .k-tile:hover / .k-btn-secondary:hover use.
export const edge = {
  sidebar: 'var(--k-sidebar-line)', // the sidebar's border-right (light --line; dark --hair)
  topbar: 'var(--k-topbar-line)', // the toolbar's border-bottom (the boards' hairline in light; --hair in dark)
  rule: 'var(--hair)', // sub-nav rules
  row: 'var(--hair)', // card headers, th
  'row-faint': 'var(--hair)', // tbody dividers
  control: 'var(--line)', // control outline
  'control-hover': 'var(--text3)',
  // .k-card outline — per theme since the calm-minimal re-land (ADOPTION.md
  // §12, delta 7): transparent in light, where the hairline RING in
  // --shadow-card is the edge; --line in dark, which keeps its border.
  card: 'var(--k-card-edge)',
  panel: 'var(--line)', // panel outline
} as const

// ── Text ──────────────────────────────────────────────────────────────────────
// Three system text levels + one app extension for `disabled` (which must sit
// below --text3 without becoming invisible). The relative ordering is
// preserved in BOTH themes by the system's own light/dark pairs — that is why
// no consumer needs to know which theme it is in.
export const ink = {
  primary: 'var(--text)',
  body: 'var(--text)',
  'strong-muted': 'var(--text)', // emphatic secondary
  secondary: 'var(--text2)',
  muted: 'var(--text2)',
  faint: 'var(--text3)', // placeholders/icons/dates
  'section-label': 'var(--text3)', // sidebar overlines
  disabled: 'var(--k-text4)',
  'on-accent': 'var(--onAccent)', // foreground on an accent/solid fill
} as const

// ── Accent ────────────────────────────────────────────────────────────────────
// The house sage is retired here: the S-Tier accent is THE accent (a cyan in
// dark, a blue in light). `soft` is the badge/bar fill, not a surface —
// .k-bar's default background.
export const accent = {
  DEFAULT: 'var(--accent)',
  hover: 'var(--accent)', // the system hovers accent fills with opacity, not a second hue
  soft: 'var(--accentSoft)',
  'health-dot': 'var(--accent)',
} as const

// ── Sensitive (dedicated family — flips independently of `warning`) ──────────
// Amber keeps its house sensitivity meaning. It rides the system's --warn pair
// (--warn reads on --warnSoft in BOTH themes), plus two extensions for the
// row wash and the banner border, which need to be fainter than --warnSoft.
export const sensitive = {
  'banner-bg': 'var(--warnSoft)',
  'text-strong': 'var(--warn)',
  indicator: 'var(--warn)', // amber dot + 2px row left-border
  wordmark: 'var(--warn)', // "SENSITIVE" wordmark
  'row-tint': 'var(--k-warn-tint)', // unresolved sensitive row bg
  'banner-border': 'var(--k-warn-line)',
  'banner-text': 'var(--warn)',
} as const

// ── attention (alias of the amber family) ────────────────────────────────────
export const attention = sensitive

// ── Semantic families ────────────────────────────────────────────────────────
// The system's status pairs are designed so `bg: *Soft` + `text: <hue>` reads
// in BOTH themes (dark: light hue on a dark tint; light: dark hue on a pale
// tint). That is why every pill below can name the same two tokens.
export const success = {
  DEFAULT: 'var(--ok)',
  strong: 'var(--k-ok-strong)',
  bg: 'var(--okSoft)',
  text: 'var(--ok)',
} as const

export const danger = {
  DEFAULT: 'var(--danger)',
  bg: 'var(--dangerSoft)',
  text: 'var(--danger)',
} as const

export const warning = {
  DEFAULT: 'var(--warn)',
  strong: 'var(--k-warn-strong)',
  bg: 'var(--warnSoft)',
  text: 'var(--warn)',
} as const

// The blue family the system omits (ADOPTION.md §3.2) — an app extension, so
// `info` never collapses into `accent` and lose its distinct meaning.
export const info = {
  DEFAULT: 'var(--k-info)',
  bg: 'var(--k-infoSoft)',
  text: 'var(--k-info)',
  'text-strong': 'var(--k-info-strong)',
} as const

// ── Status/stage maps — aliases; statusTicket adds the dot/label palette ──
export const statusRun = {
  running: { bg: surface.panel, text: ink.secondary },
  succeeded: { bg: success.bg, text: success.text },
  failed: { bg: danger.bg, text: danger.text },
  waiting: { bg: warning.bg, text: warning.text },
  queued: { bg: surface.panel, text: ink.secondary },
  active: { bg: success.bg, text: success.text },
  paused: { bg: surface.panel, text: ink.secondary },
  archived: { bg: surface.panel, text: ink.muted },
  healthy: { bg: success.bg, text: success.text },
} as const

// Dot/label form. `open` and `closed` are the two NON-status states and both
// ride neutral text tokens — `open` one step brighter than `closed`, so
// "closed" stays the quiet one in either theme.
export const statusTicket = {
  new: { dot: info.DEFAULT, label: info.DEFAULT },
  open: { dot: ink.secondary, label: ink.secondary },
  in_progress: { dot: warning.DEFAULT, label: warning.DEFAULT },
  resolved: { dot: success.DEFAULT, label: success.DEFAULT },
  closed: { dot: ink.faint, label: ink.secondary },
} as const

// Inbound-email statuses (triage queue) — PURE ALIASES, no new token.
// pending_triage is the ONE amber state: literally "needs human attention".
export const statusInboundEmail = {
  received: statusTicket.new,
  routing: statusTicket.in_progress,
  classifying: statusTicket.in_progress,
  pending_triage: { dot: sensitive.indicator, label: sensitive['text-strong'] },
  routed: statusTicket.resolved,
  completed: statusTicket.resolved,
  discarded: statusTicket.closed,
  failed: { dot: danger.DEFAULT, label: danger.text },
} as const

export const statusCandidate = {
  pending_review: { bg: warning.bg, text: warning.text },
  shortlisted: { bg: success.bg, text: success.text },
  interviewing: { bg: surface.panel, text: ink.secondary },
  offered: { bg: info.bg, text: info.text },
  rejected: { bg: danger.bg, text: danger.text },
  hired: { bg: success.bg, text: success.text },
} as const

export const statusJob = {
  draft: { bg: surface.panel, text: ink.secondary },
  active: { bg: success.bg, text: success.text },
  paused: { bg: warning.bg, text: warning.text },
  closed: { bg: danger.bg, text: danger.text },
} as const

export const statusInvoice = {
  pending_review: { bg: warning.bg, text: warning.text },
  approved: { bg: success.bg, text: success.text },
  rejected: { bg: danger.bg, text: danger.text },
  paid: { bg: info.bg, text: info.text },
} as const

export const statusExpense = {
  pending: { bg: warning.bg, text: warning.text },
  approved: { bg: success.bg, text: success.text },
  rejected: { bg: danger.bg, text: danger.text },
  paid: { bg: info.bg, text: info.text },
} as const

export const statusContact = {
  new: { bg: surface.panel, text: ink.secondary },
  contacted: { bg: info.bg, text: info.text },
  qualified: { bg: success.bg, text: success.text },
  unqualified: { bg: danger.bg, text: danger.text },
} as const

export const activityType = {
  email: { color: ink.secondary, bg: surface.panel },
  call: { color: success.DEFAULT, bg: success.bg },
  meeting: { color: info.DEFAULT, bg: info.bg },
  note: { color: warning.DEFAULT, bg: warning.bg },
  task: { color: ink.secondary, bg: surface.panel },
} as const

// Keyed by the 0097 stage vocabulary (design note §A.2). Each key keeps its
// palette slot by column position; 'unknown' is the named neutral fallback, so
// an unrecognised slug is visibly unstyled rather than borrowing a real stage's
// colour. won/lost are the SOLID badges — full-hue fill with --onAccent text,
// the one place a pill uses a solid status fill.
export const stage = {
  'new-lead': { bg: info.bg, text: info['text-strong'] },
  qualified: { bg: surface.panel, text: ink.body },
  'proposal-sent': { bg: surface.panel, text: ink.body },
  negotiation: { bg: warning.bg, text: warning.text },
  won: { bg: success.DEFAULT, text: ink['on-accent'] },
  lost: { bg: danger.DEFAULT, text: ink['on-accent'] },
  unknown: { bg: surface.panel, text: ink.body },
} as const

// ── Dots & meters ────────────────────────────────────────────────────────────
export const neutral = {
  dot: 'var(--text3)', // non-status / placeholder dot
} as const

export const confidence = {
  track: track.DEFAULT,
  high: success.DEFAULT, // confident fill
  low: warning.DEFAULT, // low-confidence fill
} as const

// ── NI eligibility finding severities (dot/label form; PURE ALIASES) ─────────
// contradicted = danger (stored facts rule the letter out); costly = the
// locked quiet-urgency amber (legal but leaves money on the table);
// unverifiable = neutral (the check could not run — explicitly NOT a pass).
export const findingSeverity = {
  contradicted: { dot: danger.DEFAULT, label: danger.text },
  costly: { dot: sensitive.indicator, label: sensitive['text-strong'] },
  unverifiable: { dot: neutral.dot, label: ink.secondary },
} as const

// ── Identity / data colors ───────────────────────────────────────────────────
// Aliases follow their families; the four identity hues the system does not
// carry (rose/pink/indigo/violet) are `--k-*` extensions, per theme.
export const dept = {
  sales: accent.DEFAULT,
  finance: success.DEFAULT,
  hr: 'var(--k-rose)',
  recruitment: info.DEFAULT,
  operations: warning.DEFAULT,
  marketing: 'var(--k-pink)',
  legal: 'var(--k-indigo)',
  bids: warning.strong,
} as const

export const avatarPalette = [
  accent.DEFAULT,
  info.DEFAULT,
  success.DEFAULT,
  warning.DEFAULT,
  danger.DEFAULT,
  dept.marketing,
  'var(--k-violet)',
] as const

export const brand = {
  linkedin: 'var(--k-brand-linkedin)',
  // The sidebar wordmark's gradient X — the full mockup's HARD 135° split
  // (.x-letter, klaxiora-crm-mockup-full.html:50-53). A token so the component
  // stays free of raw colour; applied via background-clip:text. Both stops are
  // system text tokens, so the mark re-reads correctly in light mode.
  'mark-gradient':
    'linear-gradient(135deg, var(--text) 0%, var(--text) 50%, var(--text3) 50%, var(--text3) 100%)',
} as const

// Chart palettes — written straight into recharts SVG fill attributes, where
// var() resolves (ADOPTION.md §5.2; no resolver).
export const chartCategorical = [
  accent.DEFAULT,
  info.DEFAULT,
  success.DEFAULT,
  warning.DEFAULT,
  dept.marketing,
  ink.secondary,
] as const

export const stageBar = [
  accent.DEFAULT,
  ink.secondary, // the neutral slot between the two blues
  info.DEFAULT,
  success.DEFAULT,
  warning.DEFAULT,
  dept.marketing,
  danger.DEFAULT,
] as const

// Keyed by the verbatim stage labels (DealStage). Slots follow column position
// from the pre-rename board; 'Unknown' gets the neutral placeholder dot, per
// the named-bucket rule. Bound BY STAGE, never by array index (CLAUDE.md chart
// gotcha: index binding reshuffles colours as data populates).
export const stageDot = {
  'New Lead': accent.DEFAULT,
  'Qualified': success.DEFAULT,
  'Proposal Sent': warning.DEFAULT,
  'Negotiation': info.DEFAULT,
  'Closed Won': success.strong,
  'Closed Lost': danger.DEFAULT,
  'Unknown': neutral.dot,
} as const

// Forecast-category bars (2.3b, the roll-up; RE-SKINNED in F4a, §38.14).
// Keyed by the WIRE SLUG, with '' = Uncategorised — a real bucket, so it gets
// a real slot. Bound by slug, never by array index: all five buckets always
// render (U-7), and a palette that reshuffled as zero buckets filled in would
// recolour Commit the day a rep first flagged a deal.
// F4a: the pre-F4 map (ok / accent / info / text2 / warn) FAILED the dataviz
// validator — Best case and Pipeline were two nearly identical blues (ΔE 4.4
// light, 7.8 dark, floor 15) and Omitted's gray was under the chroma floor. The
// slots are now the validated `--k-chart-*` hues in this order, meaning kept:
// Commit green (surest), Best case blue, Pipeline aqua, Omitted violet (out,
// but a hue, not a gray), Uncategorised amber (the attention hue).
export const forecastCategoryBar = {
  commit: 'var(--k-chart-green)',
  best_case: 'var(--k-chart-blue)',
  pipeline: 'var(--k-chart-aqua)',
  omitted: 'var(--k-chart-violet)',
  '': 'var(--k-chart-amber)',
} as const

// F4a (spec §3.4): the Attribution split bar. Linked won value in the chart
// blue, unlinked in amber — the same amber as Uncategorised, the same meaning
// (value nobody is answerable for). Validated as a pair in both modes; the
// light amber's sub-3:1 contrast is relieved by the captions under the bar.
export const forecastAttributionSplit = {
  attributed: 'var(--k-chart-blue)',
  unattributed: 'var(--k-chart-amber)',
} as const

// F4a (spec §3.5): the look-ahead's month bars. The spec shades each month by
// its position (light / mid / dark); months are ONE series, so one colour —
// a shade by position would encode nothing (color encodes meaning).
export const forecastLookAheadBar = 'var(--k-chart-blue)'

// Suggested-category pills (F3, spec §3.3), keyed by the wire slug. The
// spec's own pairs: Commit = ok, Best case = the info blue, Pipeline = the
// subtle ground with the secondary ink, Omitted = danger — the pill says what
// the rule proposes, so Omitted (out of the forecast) reads as a warning to
// look at, not as neutral. Bound by slug, never by index.
export const forecastSuggestionPill = {
  commit: { bg: success.bg, fg: success.text },
  best_case: { bg: info.bg, fg: info.text },
  pipeline: { bg: surface.panel, fg: ink.secondary },
  omitted: { bg: danger.bg, fg: danger.text },
} as const

// Forecast hero rings (F2, spec §3.2): the outer ring is WON, in the accent;
// the inner ring is the CALENDAR — the share of the period elapsed — in the
// spec's indigo, so the two concentric rings never read as one hue (accent and
// info are both blue in light). --k-indigo is defined for both themes.
export const forecastRing = {
  won: accent.DEFAULT,
  elapsed: 'var(--k-indigo)',
} as const

// ── Overlay / shadows ─────────────────────────────────────────────────────────
export const overlay = {
  scrim: 'var(--k-scrim)', // a backdrop, not a shadow
} as const

// SHADOWS: the system ships exactly ONE, on cards. Every other key stays
// 'none' so consumers resolve without touching call sites.
export const shadow = {
  card: 'var(--shadow-card)',
  sidebar: 'none',
  header: 'none',
  'card-flat': 'none',
  tooltip: 'none',
  deal: 'none',
  'deal-hover': 'none',
  'schedule-hint': 'none',
} as const

// ── Focus ─────────────────────────────────────────────────────────────────────
// The ADDED accessibility affordance (ADOPTION.md §3.3 — the system ships no
// focus rule at all). Solid and PRE-MIXED on purpose: tokens.ts now re-exports
// var() strings, and Tailwind's `/NN` alpha syntax cannot apply to those, so
// the old `focus:ring-accent/20` pattern could not survive. The global
// :focus-visible rule in klaxiora.css is the primary carrier; this key exists
// for components that need to draw the ring themselves.
export const focus = {
  ring: 'var(--focus-ring)',
} as const

// ── Font ──────────────────────────────────────────────────────────────────────
// `sans` is THE app stack: tailwind fontFamily.sans reads it. The families are
// self-hosted (webapp/public/fonts, @font-face in src/styles/fonts.css) — no
// network font dependency at runtime or in the visual-baseline path.
// printSans is TracePanel's standalone print document, which is a SEPARATE
// document and cannot resolve this app's CSS vars — it interpolates a literal.
export const font = {
  sans: 'var(--font-sans)',
  // The Tailwind `font-mono` utility's stack. NOT var(--font-mono) directly:
  // --k-tw-mono is theme-gated in klaxiora.css — it IS var(--font-mono) in a
  // themed document (utility and k-* classes cannot disagree), and the
  // pre-adoption JetBrains stack at bare :root, so unmigrated Mission Control
  // keeps rendering exactly as before.
  mono: 'var(--k-tw-mono)',
  printSans: "'Instrument Sans', system-ui, sans-serif",
} as const

// ── Type scale — 11/12/13/14/16/20/24 + 34/36, line-heights on the 4px grid ──
// Tailwind fontSize form: [size, lineHeight] or [size, { lineHeight,
// letterSpacing }]. The numeric steps are UNCHANGED since Phase 0. The three
// NAMED steps the calm-minimal spec sizes moved onto it at the re-land
// (ADOPTION.md §12, delta 6; pinned to docs/design/calm-minimal/tokens.css):
// page-title 34px/-0.022em (was 24), metric 36px/-0.025em (was 24),
// section-title 20px/-0.015em (size unchanged, tracking added). Weights stay
// at the call site — a fontWeight in the tuple would fight the utility beside
// it. detail-title (drawer subjects) stays 20: the spec has no drawer.
export const fontSize = {
  '11': ['11px', '16px'],
  '12': ['12px', '16px'],
  '13': ['13px', '20px'],
  '14': ['14px', '20px'],
  '16': ['16px', '24px'],
  '20': ['20px', '28px'],
  '24': ['24px', '32px'],
  overline: ['11px', '16px'],
  'detail-title': ['20px', '28px'], // drawer subjects
  'section-title': ['20px', { lineHeight: '28px', letterSpacing: '-0.015em' }],
  'page-title': ['34px', { lineHeight: '40px', letterSpacing: '-0.022em' }],
  metric: ['36px', { lineHeight: '40px', letterSpacing: '-0.025em' }],
} as const

// ── Radius / layout ───────────────────────────────────────────────────────────
// `control` maps to --k-r-control (= --r-nav), NOT --r-pill: the system's
// control radius is a capsule, but a blanket flip would turn textareas and
// icon chips into pills. Per-component capsule adoption is slices 2–6.
export const radius = {
  card: 'var(--r-card)',
  nav: 'var(--r-nav)',
  control: 'var(--k-r-control)',
  tile: 'var(--r-tile)', // nested tiles inside a panel — empty boxes, skeletons
} as const

// The shell's two dimensions. Each is ALSO a custom property in klaxiora.css
// (--k-sidebar-w / --k-topbar-h, delta 6) because .k-shell and .k-topbar read
// them there; tokens.test.ts pins the pairs equal. Tailwind's spacing scale
// takes these (spacing.sidebar / spacing.topbar) for the .dash-shell grid.
export const layout = {
  'sidebar-w': '260px', // .k-shell grid-template-columns; the spec's <nav>
  'topbar-h': '64px', // .k-topbar min-height; the spec's <header>
} as const

// Sales Home (redesign slice 3, ADOPTION.md §15): the boards' inverted "Up
// next" card and the attention mark, as custom properties in klaxiora.css
// (both themes carry the same values — the card is dark by design).
export const home = {
  invertedBg: 'var(--k-home-inverted-bg)',
  invertedSurface: 'var(--k-home-inverted-surface)',
  invertedInk: 'var(--k-home-inverted-ink)',
  invertedMuted: 'var(--k-home-inverted-muted)',
  invertedAccent: 'var(--k-home-inverted-accent)',
  attention: 'var(--k-attention)',
} as const

// Slice 4 (ADOPTION.md §16): the Pipeline board's lane surfaces and radius, as
// custom properties in klaxiora.css (light from Pipeline.dc.html; dark on the
// hybrid's panel2 / okSoft; the radius theme-invariant).
export const pipeline = {
  laneBg: 'var(--k-lane-bg)',
  laneWonBg: 'var(--k-lane-won-bg)',
  laneRadius: 'var(--k-lane-r)',
} as const

export const tracking = {
  overline: '0.09em', // section headers / wordmarks
} as const
